import React, { useCallback, useEffect, useRef, useState } from 'react';
import crmClient from './crmClient';
import RichTextEditor from './RichTextEditor';
import { stageLabel, STAGE_TONES, formatCurrency, formatNumber, formatDate, formatDateTime, daysSince, formatAddress, formatPhone } from './format';

const MetricTile = ({ label, value, sub }) => (
  <div className="crm-metric-tile">
    <span>{label}</span>
    <strong>{value}</strong>
    {sub && <small>{sub}</small>}
  </div>
);

const IntelRow = ({ label, value }) =>
  value === null || value === undefined || value === '' ? null : (
    <div className="crm-intel-row">
      <span className="crm-intel-label">{label}</span>
      <span className="crm-intel-value">{value}</span>
    </div>
  );

// Renders the AI-prospecting research captured when the company was added.
const ProspectingIntel = ({ intel }) => {
  if (!intel || typeof intel !== 'object') {
    return null;
  }
  const ai = intel.ai_analysis && typeof intel.ai_analysis === 'object' ? intel.ai_analysis : {};
  const titles = Array.isArray(intel.recommended_titles) ? intel.recommended_titles : [];
  const urls = Array.isArray(intel.source_urls) ? intel.source_urls : [];
  const breakdown = intel.score_breakdown && typeof intel.score_breakdown === 'object' ? intel.score_breakdown : {};
  // Surface any extra narrative fields the model returned beyond why_gophr.
  const aiExtra = Object.keys(ai).filter(
    k => k !== 'why_gophr' && (typeof ai[k] === 'string' || typeof ai[k] === 'number') && String(ai[k]).trim()
  );
  const prettify = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <section className="crm-detail-section">
      <h3>
        AI Prospecting intel
        {intel.gophr_fit_score != null && <span className="crm-intel-score">Fit score {intel.gophr_fit_score}</span>}
      </h3>

      {ai.why_gophr && <p className="crm-intel-why">{ai.why_gophr}</p>}

      {intel.recommended_service && <IntelRow label="Recommended service" value={intel.recommended_service} />}
      {intel.likely_department && <IntelRow label="Likely department" value={intel.likely_department} />}
      {titles.length > 0 && <IntelRow label="Recommended titles" value={titles.join(', ')} />}
      {intel.description && <IntelRow label="About" value={intel.description} />}
      {intel.company_size && <IntelRow label="Company size" value={intel.company_size} />}
      {intel.num_locations != null && <IntelRow label="Locations" value={intel.num_locations} />}
      <IntelRow label="Industry" value={[intel.industry, intel.subcategory].filter(Boolean).join(' / ')} />
      {intel.legal_name && <IntelRow label="Legal name" value={intel.legal_name} />}
      {intel.parent_name && <IntelRow label="Parent" value={intel.parent_name} />}
      {intel.division && <IntelRow label="Division" value={intel.division} />}
      <IntelRow label="Address" value={[intel.address, intel.city, intel.state, intel.zip].filter(Boolean).join(', ')} />

      {aiExtra.map(k => (
        <IntelRow key={k} label={prettify(k)} value={String(ai[k])} />
      ))}

      {Object.keys(breakdown).length > 0 && (
        <div className="crm-intel-breakdown">
          <span className="crm-intel-label">Fit score breakdown</span>
          <ul>
            {Object.keys(breakdown).map(k => (
              <li key={k}>
                {prettify(k)}: <strong>{typeof breakdown[k] === 'object' ? JSON.stringify(breakdown[k]) : String(breakdown[k])}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {urls.length > 0 && (
        <div className="crm-intel-sources">
          <span className="crm-intel-label">Sources</span>
          <ul>
            {urls.map((u, i) => (
              <li key={i}>
                <a href={u} target="_blank" rel="noreferrer">
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="crm-intel-meta">
        {intel.source_provider ? `Source: ${intel.source_provider}` : 'AI Prospecting'}
        {intel.captured_at ? ` · captured ${new Date(intel.captured_at).toLocaleDateString()}` : ''}
      </p>
    </section>
  );
};

const CompanyDetail = ({ companyId, meta, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [stageChoice, setStageChoice] = useState('');
  const [stageReason, setStageReason] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [lostNotes, setLostNotes] = useState('');
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', phone: '', email: '', title: '' });
  const [showAddContact, setShowAddContact] = useState(false);
  const noteRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [emailForm, setEmailForm] = useState({ contactId: '', templateId: '', subject: '', bodyHtml: '', sendAs: 'gophr', replyTo: '' });
  const [emailStatus, setEmailStatus] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Live preview of the email with the selected contact's real merge fields
  // (first name, company, etc.) filled in — only fetched when the user opens it.
  useEffect(() => {
    if (!showPreview || !emailForm.contactId || (!emailForm.subject && !emailForm.bodyHtml)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      crmClient
        .previewContactEmail(emailForm.contactId, {
          subject: emailForm.subject,
          body_html: emailForm.bodyHtml,
          template_id: emailForm.templateId || null,
        })
        .then(payload => setEmailPreview((payload && payload.preview) || null))
        .catch(() => setEmailPreview(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [showPreview, emailForm.contactId, emailForm.subject, emailForm.bodyHtml, emailForm.templateId]);

  useEffect(() => {
    crmClient
      .listTemplates()
      .then(payload => setTemplates((payload && payload.templates) || []))
      .catch(() => setTemplates([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .getCompany(companyId)
      .then(payload => {
        setDetail(payload);
        setStageChoice((payload && payload.company && payload.company.crm_stage) || '');
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load account.');
        setLoading(false);
      });
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = () => {
    load();
    if (onChanged) {
      onChanged();
    }
  };

  const guard = (action, onSuccess) => {
    setBusy(true);
    setError(null);
    action()
      .then(() => {
        setBusy(false);
        if (onSuccess) {
          onSuccess();
        }
        afterChange();
      })
      .catch(err => {
        setBusy(false);
        const message = err && err.response && err.response.data ? err.response.data.message : 'Action failed.';
        setError(message);
      });
  };

  const applyTemplate = templateId => {
    const tpl = templates.find(t => t.crm_email_template_id === templateId);
    setEmailForm(f => ({
      ...f,
      templateId,
      subject: tpl ? tpl.subject || '' : f.subject,
      bodyHtml: tpl ? tpl.body_html || tpl.body || '' : f.bodyHtml,
    }));
  };

  const sendContactEmail = () => {
    if (!emailForm.contactId) {
      setEmailStatus({ error: 'Select a contact to email.' });
      return;
    }
    if (!emailForm.subject || !emailForm.bodyHtml) {
      setEmailStatus({ error: 'Add a subject and a message.' });
      return;
    }
    setSendingEmail(true);
    setEmailStatus(null);
    crmClient
      .sendContactEmail(emailForm.contactId, {
        subject: emailForm.subject,
        body_html: emailForm.bodyHtml,
        template_id: emailForm.templateId || null,
        send_as: emailForm.sendAs,
        reply_to: emailForm.sendAs === 'me' ? emailForm.replyTo || null : null,
      })
      .then(res => {
        setSendingEmail(false);
        if (res && res.ok) {
          setEmailStatus({
            ok: true,
            message: res.simulated
              ? 'Recorded (no email provider configured).'
              : `Email sent${res.reply_to ? ` · replies go to ${res.reply_to}` : ''}.`,
          });
          setEmailForm(f => ({ ...f, subject: '', bodyHtml: '', templateId: '' }));
          afterChange();
        } else {
          setEmailStatus({ error: (res && res.error) || 'Unable to send the email.' });
        }
      })
      .catch(err => {
        setSendingEmail(false);
        setEmailStatus({ error: (err && err.response && err.response.data && err.response.data.message) || 'Unable to send the email.' });
      });
  };

  const addContact = () => {
    if (!newContact.first_name && !newContact.phone && !newContact.email) {
      setError('Add a name, phone, or email for the contact.');
      return;
    }
    guard(
      () =>
        crmClient.createContact({
          crm_company_id: companyId,
          first_name: newContact.first_name || null,
          last_name: newContact.last_name || null,
          phone: newContact.phone || null,
          email: newContact.email || null,
          title: newContact.title || null,
          contact_types: ['prospect'],
          lead_source: 'Manual entry',
        }),
      () => {
        setNewContact({ first_name: '', last_name: '', phone: '', email: '', title: '' });
        setShowAddContact(false);
      }
    );
  };

  const removeContact = contact => {
    const label = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || contact.phone || 'this contact';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${label} from this account?`)) {
      return;
    }
    guard(() => crmClient.deleteContact(contact.crm_contact_id));
  };

  const salespeople = (meta && meta.salespeople) || [];

  // Detect an in-progress @token immediately before the caret for autocomplete.
  const handleNoteChange = event => {
    const value = event.target.value;
    setNoteText(value);
    const caret = event.target.selectionStart || value.length;
    const match = value.slice(0, caret).match(/@([\w.@+-]*)$/);
    if (match) {
      setMentionQuery(match[1].toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
      setMentionQuery('');
    }
  };

  const mentionMatches = mentionOpen
    ? salespeople.filter(u => (u.email || '').toLowerCase().includes(mentionQuery)).slice(0, 6)
    : [];

  const insertMention = user => {
    const el = noteRef.current;
    const caret = el ? el.selectionStart : noteText.length;
    const upto = noteText.slice(0, caret).replace(/@([\w.@+-]*)$/, `@${user.email} `);
    const next = upto + noteText.slice(caret);
    setNoteText(next);
    setMentionOpen(false);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(upto.length, upto.length);
      }
    });
  };

  const saveNote = () => {
    const text = noteText.trim();
    if (!text) {
      return;
    }
    const mentions = salespeople.filter(u => u.email && noteText.includes(`@${u.email}`)).map(u => u.auth0_id);
    guard(() => crmClient.addActivity(companyId, { activity_type: 'note', description: text, mentions }), () => setNoteText(''));
  };

  // Render note text with @mentions visually highlighted.
  const renderNoteText = text => {
    if (!text) {
      return null;
    }
    return String(text)
      .split(/(@[\w.@+-]+)/g)
      .map((part, index) =>
        part.startsWith('@') ? (
          <span key={index} className="crm-mention-tag">
            {part}
          </span>
        ) : (
          part
        )
      );
  };

  if (loading) {
    return (
      <aside className="crm-detail">
        <div className="crm-empty">Loading account…</div>
      </aside>
    );
  }

  if (!detail || !detail.company) {
    return (
      <aside className="crm-detail">
        <div className="crm-empty crm-empty--error">{error || 'Account not found.'}</div>
        <button type="button" className="crm-btn" onClick={onClose}>
          Close
        </button>
      </aside>
    );
  }

  const { company, activities, stage_history: stageHistory, contacts } = detail;
  const lastOrderDays = daysSince(company.last_order_date);
  const categories = (meta && meta.categories) || {};
  const subcategories = company.customer_category ? categories[company.customer_category] || [] : [];

  return (
    <aside className="crm-detail">
      <div className="crm-detail-head">
        <div>
          <div className={`crm-stage-pill crm-stage-pill--${STAGE_TONES[company.crm_stage] || 'slate'}`}>
            {stageLabel(company.crm_stage)}
            {company.stage_override ? <span className="crm-override-flag">manual</span> : null}
          </div>
          <h2>{company.business_name}</h2>
          <p className="crm-detail-sub">
            {company.city || '—'}
            {company.state ? `, ${company.state}` : ''} · {company.customer_category || 'Unclassified'}
            {company.customer_subcategory ? ` / ${company.customer_subcategory}` : ''}
          </p>
          {formatAddress(company) ? <p className="crm-detail-address">{formatAddress(company)}</p> : null}
          {company.main_phone ? (
            <p className="crm-detail-address">
              <a className="crm-phone-link" href={`tel:${company.main_phone}`}>{formatPhone(company.main_phone)}</a>
            </p>
          ) : null}
        </div>
        <button type="button" className="crm-icon-btn" onClick={onClose} aria-label="Close account">
          ✕
        </button>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      <div className="crm-metric-grid">
        <MetricTile label="Lifetime revenue" value={formatCurrency(company.lifetime_revenue)} />
        <MetricTile label="Lifetime orders" value={formatNumber(company.lifetime_orders)} />
        <MetricTile label="Avg order value" value={formatCurrency(company.average_order_value)} />
        <MetricTile
          label="Last order"
          value={formatDate(company.last_order_date)}
          sub={lastOrderDays != null ? `${lastOrderDays} days ago` : 'No orders yet'}
        />
        <MetricTile label="Orders · 30d" value={formatNumber(company.orders_last_30_days)} />
        <MetricTile label="Orders · 6mo" value={formatNumber(company.orders_last_6_months)} />
        <MetricTile label="Revenue · 30d" value={formatCurrency(company.revenue_last_30_days)} />
        <MetricTile label="Revenue · 12mo" value={formatCurrency(company.revenue_last_12_months)} />
      </div>

      <div className="crm-detail-actions">
        <button type="button" className="crm-btn" disabled={busy} onClick={() => guard(() => crmClient.recompute(company.crm_company_id))}>
          Recompute from orders
        </button>
        {company.stage_override && (
          <button
            type="button"
            className="crm-btn crm-btn--ghost"
            disabled={busy}
            onClick={() => guard(() => crmClient.setStage(company.crm_company_id, { release_override: true }))}
          >
            Return to automatic control
          </button>
        )}
      </div>

      <ProspectingIntel intel={company.prospecting_intel} />

      <section className="crm-detail-section">
        <h3>Owner</h3>
        <div className="crm-field-row">
          <label>
            <span>Assigned salesperson</span>
            <select
              value={company.assigned_salesperson_auth0_id || ''}
              disabled={busy}
              onChange={event =>
                guard(() => crmClient.updateCompany(company.crm_company_id, { assigned_salesperson_auth0_id: event.target.value || null }))
              }
            >
              <option value="">Unassigned</option>
              {((meta && meta.salespeople) || []).map(s => (
                <option key={s.auth0_id || s.email} value={s.auth0_id || ''}>
                  {s.email}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="crm-detail-section">
        <h3>Classification</h3>
        <div className="crm-detail-actions">
          <button
            type="button"
            className="crm-btn crm-btn--ghost crm-btn--sm"
            disabled={busy}
            onClick={() => guard(() => crmClient.classifyCompany(company.crm_company_id, { force: true }))}
          >
            Auto-classify
          </button>
          {company.ai_classification_confidence != null && (
            <span className="crm-muted">AI confidence: {Math.round(Number(company.ai_classification_confidence) * 100)}%</span>
          )}
        </div>
        <div className="crm-field-row">
          <label>
            <span>Category</span>
            <select
              value={company.customer_category || ''}
              disabled={busy}
              onChange={event =>
                guard(() => crmClient.updateCompany(company.crm_company_id, { customer_category: event.target.value || null, customer_subcategory: null }))
              }
            >
              <option value="">Unclassified</option>
              {Object.keys(categories).map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Subcategory</span>
            <select
              value={company.customer_subcategory || ''}
              disabled={busy || !subcategories.length}
              onChange={event =>
                guard(() => crmClient.updateCompany(company.crm_company_id, { customer_subcategory: event.target.value || null }))
              }
            >
              <option value="">—</option>
              {subcategories.map(sub => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="crm-detail-section">
        <h3>Lifecycle stage</h3>
        <div className="crm-field-row">
          <label>
            <span>Set stage (manual override)</span>
            <select value={stageChoice} onChange={event => setStageChoice(event.target.value)} disabled={busy}>
              {((meta && meta.stages) || []).map(stage => (
                <option key={stage} value={stage}>
                  {stageLabel(stage)}
                </option>
              ))}
            </select>
          </label>
          <label className="crm-field-grow">
            <span>Reason</span>
            <input type="text" value={stageReason} onChange={event => setStageReason(event.target.value)} placeholder="Why is this override needed?" />
          </label>
        </div>
        <button
          type="button"
          className="crm-btn"
          disabled={busy || !stageReason || stageChoice === company.crm_stage}
          onClick={() =>
            guard(() => crmClient.setStage(company.crm_company_id, { stage: stageChoice, reason: stageReason }), () => setStageReason(''))
          }
        >
          Apply stage override
        </button>
      </section>

      {company.crm_stage !== 'lost' && (
        <section className="crm-detail-section">
          <h3>Mark as lost</h3>
          <div className="crm-field-row">
            <label>
              <span>Lost reason</span>
              <select value={lostReason} onChange={event => setLostReason(event.target.value)} disabled={busy}>
                <option value="">Select…</option>
                {((meta && meta.lost_reasons) || []).map(reason => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </label>
            <label className="crm-field-grow">
              <span>Notes</span>
              <input type="text" value={lostNotes} onChange={event => setLostNotes(event.target.value)} placeholder="Optional context" />
            </label>
          </div>
          <button
            type="button"
            className="crm-btn crm-btn--danger"
            disabled={busy || !lostReason}
            onClick={() => guard(() => crmClient.markLost(company.crm_company_id, { lost_reason: lostReason, lost_notes: lostNotes }))}
          >
            Mark lost
          </button>
        </section>
      )}

      <section className="crm-detail-section">
        <h3>Contacts ({(contacts || []).length})</h3>
        {(contacts || []).length === 0 ? (
          <p className="crm-muted">No contacts linked yet.</p>
        ) : (
          <ul className="crm-contact-list">
            {contacts.map(contact => (
              <li key={contact.crm_contact_id}>
                <div className="crm-contact-info">
                  <strong>
                    {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || 'Unnamed contact'}
                  </strong>
                  <small>
                    {contact.title ? `${contact.title} · ` : ''}
                    {contact.email || '—'}
                  </small>
                  {contact.phone && (
                    <small className="crm-contact-phone">
                      <a href={`tel:${contact.phone}`}>📞 {contact.phone}</a>
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="crm-btn crm-btn--danger crm-btn--sm"
                  disabled={busy}
                  onClick={() => removeContact(contact)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {showAddContact ? (
          <div className="crm-add-contact">
            <div className="crm-field-row">
              <input
                placeholder="First name"
                value={newContact.first_name}
                onChange={event => setNewContact({ ...newContact, first_name: event.target.value })}
              />
              <input
                placeholder="Last name"
                value={newContact.last_name}
                onChange={event => setNewContact({ ...newContact, last_name: event.target.value })}
              />
            </div>
            <div className="crm-field-row">
              <input
                placeholder="Phone"
                value={newContact.phone}
                onChange={event => setNewContact({ ...newContact, phone: event.target.value })}
              />
              <input
                placeholder="Email"
                value={newContact.email}
                onChange={event => setNewContact({ ...newContact, email: event.target.value })}
              />
            </div>
            <input
              placeholder="Title (optional)"
              value={newContact.title}
              onChange={event => setNewContact({ ...newContact, title: event.target.value })}
            />
            <div className="crm-detail-actions">
              <button type="button" className="crm-btn crm-btn--sm" disabled={busy} onClick={addContact}>
                Save contact
              </button>
              <button
                type="button"
                className="crm-btn crm-btn--ghost crm-btn--sm"
                onClick={() => {
                  setShowAddContact(false);
                  setNewContact({ first_name: '', last_name: '', phone: '', email: '', title: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm crm-add-contact-btn" onClick={() => setShowAddContact(true)}>
            + Add contact
          </button>
        )}
      </section>

      <section className="crm-detail-section">
        <h3>Email a contact</h3>
        {(contacts || []).filter(c => c.email).length === 0 ? (
          <p className="crm-muted">Add a contact with an email address to send from here.</p>
        ) : (
          <div className="crm-email-composer">
            <div className="crm-field-row">
              <label className="crm-field-grow">
                <span>To</span>
                <select
                  value={emailForm.contactId}
                  onChange={event => setEmailForm({ ...emailForm, contactId: event.target.value })}
                >
                  <option value="">Select contact…</option>
                  {(contacts || [])
                    .filter(c => c.email)
                    .map(c => (
                      <option key={c.crm_contact_id} value={c.crm_contact_id}>
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email} · {c.email}
                        {c.do_not_email ? ' (do-not-email)' : ''}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Template</span>
                <select value={emailForm.templateId} onChange={event => applyTemplate(event.target.value)}>
                  <option value="">— None —</option>
                  {templates.map(t => (
                    <option key={t.crm_email_template_id} value={t.crm_email_template_id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="crm-field-grow">
              <span>Send from</span>
              <select
                value={emailForm.sendAs}
                onChange={event =>
                  setEmailForm({
                    ...emailForm,
                    sendAs: event.target.value,
                    replyTo:
                      event.target.value === 'me' && !emailForm.replyTo && meta && meta.current_user
                        ? meta.current_user.email || ''
                        : emailForm.replyTo,
                  })
                }
              >
                <option value="gophr">Gophr (support@gophr.app)</option>
                <option value="me">My email — send replies to me</option>
              </select>
            </label>

            {emailForm.sendAs === 'me' && (
              <label className="crm-field-grow">
                <span>Reply-to email (where replies go)</span>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={emailForm.replyTo}
                  onChange={event => setEmailForm({ ...emailForm, replyTo: event.target.value })}
                />
              </label>
            )}

            <label className="crm-field-grow">
              <span>Subject</span>
              <input value={emailForm.subject} onChange={event => setEmailForm({ ...emailForm, subject: event.target.value })} />
            </label>

            {/* Not a <label>: wrapping a contentEditable in <label> collapses the caret on click. */}
            <div className="crm-field-grow crm-rte-field">
              <span>Message</span>
              <RichTextEditor
                value={emailForm.bodyHtml}
                onChange={html => setEmailForm(f => ({ ...f, bodyHtml: html }))}
                onUpload={file => crmClient.uploadAsset(file)}
              />
            </div>

            <button
              type="button"
              className="crm-btn crm-btn--ghost crm-btn--sm"
              disabled={!emailForm.contactId || (!emailForm.subject && !emailForm.bodyHtml)}
              onClick={() => setShowPreview(v => !v)}
            >
              {showPreview ? 'Hide preview' : 'Preview email'}
            </button>

            {showPreview && emailPreview && (
              <div className="crm-email-preview">
                <div className="crm-email-preview-label">
                  👁 Preview — how{' '}
                  {(() => {
                    const pc = (contacts || []).find(c => c.crm_contact_id === emailForm.contactId);
                    return (pc && pc.first_name) || 'this contact';
                  })()}{' '}
                  will see it
                </div>
                <div className="crm-email-preview-subject">
                  <strong>Subject:</strong> {emailPreview.subject || '—'}
                </div>
                <iframe title="Email preview" className="crm-email-preview-frame" srcDoc={emailPreview.html} />
              </div>
            )}

            {emailStatus && (
              <div className={emailStatus.ok ? 'crm-preview-banner' : 'crm-inline-error'}>
                {emailStatus.ok ? emailStatus.message : emailStatus.error}
              </div>
            )}

            <div className="crm-detail-actions">
              <button type="button" className="crm-btn" disabled={sendingEmail} onClick={sendContactEmail}>
                {sendingEmail ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="crm-detail-section">
        <h3>Notes &amp; messages</h3>
        <div className="crm-note-composer">
          <textarea
            ref={noteRef}
            value={noteText}
            onChange={handleNoteChange}
            onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
            placeholder="Write a note or ask a teammate — type @ to tag someone"
            rows={3}
          />
          {mentionOpen && mentionMatches.length > 0 && (
            <ul className="crm-mention-menu">
              {mentionMatches.map(user => (
                <li key={user.auth0_id}>
                  <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => insertMention(user)}>
                    @{user.email}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="button" className="crm-btn" disabled={busy || !noteText.trim()} onClick={saveNote}>
          Post note
        </button>
      </section>

      <section className="crm-detail-section">
        <h3>Activity timeline</h3>
        <ul className="crm-timeline">
          {(activities || []).length === 0 && <li className="crm-muted">No activity recorded.</li>}
          {(activities || []).map(activity => (
            <li key={activity.crm_activity_id}>
              <span className="crm-timeline-dot" />
              <div>
                <strong>{activity.activity_type.replace(/_/g, ' ')}</strong>
                {activity.description && <p>{renderNoteText(activity.description)}</p>}
                <small>{formatDateTime(activity.occurred_at)}</small>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="crm-detail-section">
        <h3>Stage history</h3>
        <ul className="crm-timeline crm-timeline--compact">
          {(stageHistory || []).length === 0 && <li className="crm-muted">No stage changes.</li>}
          {(stageHistory || []).map(entry => (
            <li key={entry.crm_stage_history_id}>
              <span className="crm-timeline-dot" />
              <div>
                <strong>
                  {stageLabel(entry.previous_stage)} → {stageLabel(entry.new_stage)}
                  {entry.is_manual ? ' (manual)' : ''}
                </strong>
                {entry.reason && <p>{entry.reason}</p>}
                <small>{formatDateTime(entry.created_at)}</small>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
};

export default CompanyDetail;
